import { getSanityVideoEmbedId, type SanityImageCrop, type SanityImageHotspot, type SanityImageValue, type SanityMediaValue, type SanityVideoValue } from "@ouster/shared";
import { type SanityImageEdit, parseSanityAssetDimensions, resolveSanityCropRect } from "~/features/sanity/media/image-url";
import type { Props as ImageProps } from "~/features/ui/image";
import type { Provider as VideoProvider } from "~/features/ui/video/external-video";
type SanityImageSource = {
  _id?: string | null;
  altText?: string | null;
  alt?: string | null;
  crop?: SanityImageCrop | null;
  description?: string | null;
  dimensions?: {
    height?: number | null;
    width?: number | null;
  } | null;
  height?: number | null;
  hotspot?: SanityImageHotspot | null;
  lqip?: string | null;
  mimeType?: string | null;
  originalFilename?: string | null;
  src?: string | null;
  title?: string | null;
  type?: string | null;
  url?: string | null;
  width?: number | null;
};
type SanityImageLike = {
  asset?: SanityImageSource | null;
  caption?: string | null;
  image?: {
    source?: SanityImageSource | null;
  } | null;
  imageAsset?: SanityImageSource[] | null;
  isDarkened?: boolean | null;
  kind?: string;
  src?: string | null;
  url?: string | null;
};
export type SanityImageAdapterResult = Pick<ImageProps, "alt" | "focalPoint" | "height" | "imageEdit" | "mimeType" | "oriHeight" | "oriWidth" | "src" | "width">;
export type CraftAsset = {
  __typename?: string;
  alt?: string;
  focalPoint?: number[];
  imageEdit?: SanityImageEdit;
  mimeType?: string;
  oriHeight?: number;
  oriWidth?: number;
  palette?: string;
  src?: string;
  type?: string;
};
export type CraftMediaBlock = {
  __typename?: string;
  caption?: string | null;
  coverMedia?: CraftAsset[];
  imageAsset?: CraftAsset[];
  isDarkened?: boolean | null;
  posterImage?: Array<{
    src?: string;
  }>;
  showControls?: boolean;
  videoAsset?: CraftAsset[];
  videoId?: string;
  videoProvider?: VideoProvider;
  videoUrl?: string;
};
function getImageSource(media?: SanityMediaValue | SanityImageValue | null): SanityImageSource | undefined {
  const value = media as SanityImageLike | undefined;
  if (!value) {
    return undefined;
  }
  if (value.url || value.src) {
    return value;
  }
  return value.image?.source || value.asset || value.imageAsset?.[0] || undefined;
}
function clampFraction(value: number) {
  return Math.min(1, Math.max(0, value));
}
export function adaptSanityImage(media?: SanityMediaValue | SanityImageValue | null): SanityImageAdapterResult | undefined {
  const source = getImageSource(media);
  const src = source?.url || source?.src;
  if (!src) {
    return undefined;
  }
  const assetDimensions = parseSanityAssetDimensions(source._id);
  const sourceWidth = source.dimensions?.width || assetDimensions?.width || undefined;
  const sourceHeight = source.dimensions?.height || assetDimensions?.height || undefined;
  const imageEdit: SanityImageEdit | undefined = (source.crop || source.hotspot) && sourceWidth && sourceHeight ? {
    crop: source.crop,
    hotspot: source.hotspot,
    sourceWidth,
    sourceHeight
  } : undefined;

  // The rendered image is the cropped box, so the intrinsic dimensions we report
  // (and every aspect-ratio derivation downstream) must describe the crop, not the
  // original asset.
  const cropRect = resolveSanityCropRect(imageEdit);
  const width = cropRect?.width || sourceWidth;
  const height = cropRect?.height || sourceHeight;

  // Both `fp-x`/`fp-y` and CSS `object-position` operate on the cropped image, so
  // express the hotspot relative to the crop rect rather than the original asset.
  const hotspot = source.hotspot;
  const focalPoint = hotspot && sourceWidth && sourceHeight ? cropRect ? [clampFraction((hotspot.x * sourceWidth - cropRect.left) / cropRect.width), clampFraction((hotspot.y * sourceHeight - cropRect.top) / cropRect.height)] : [hotspot.x, hotspot.y] : undefined;
  return {
    alt: source.alt || source.altText || source.description || source.title || "",
    focalPoint,
    height,
    imageEdit,
    mimeType: source.mimeType || source.type || undefined,
    oriHeight: height,
    oriWidth: width,
    src,
    width
  };
}
function inferImageMimeType(filename?: string | null): string {
  const extension = filename?.split("?")[0]?.split("#")[0]?.split(".").pop()?.toLowerCase();
  switch (extension) {
    case "avif":
      return "image/avif";
    case "gif":
      return "image/gif";
    case "jpeg":
    case "jpg":
      return "image/jpeg";
    case "png":
      return "image/png";
    case "svg":
      return "image/svg+xml";
    case "webp":
      return "image/webp";
    default:
      return "image/*";
  }
}
export function toCraftAsset(media?: SanityMediaValue | SanityImageValue | null): CraftAsset | undefined {
  const image = adaptSanityImage(media);
  const source = getImageSource(media);
  if (!image?.src) {
    return undefined;
  }
  const mimeType = image.mimeType || inferImageMimeType(source?.originalFilename);
  return {
    __typename: "default_Asset",
    alt: image.alt,
    focalPoint: image.focalPoint,
    imageEdit: image.imageEdit,
    mimeType,
    oriHeight: image.oriHeight,
    oriWidth: image.oriWidth,
    src: image.src,
    type: mimeType
  };
}
export function toCraftThumbnail(media?: SanityMediaValue | SanityImageValue | null): CraftAsset | undefined {
  const value = media as SanityMediaValue | undefined;
  if (value?.kind === "video") {
    return toCraftAsset(value.video?.cover);
  }
  return toCraftAsset(value);
}
function getVideoId(video: SanityVideoValue): string | undefined {
  const src = video.source?.src;
  if (!src) {
    return undefined;
  }
  if (video.kind === "mux") {
    return getSanityVideoEmbedId(src, "mux");
  }
  if (video.kind === "youtube") {
    return getSanityVideoEmbedId(src, "youtube") || undefined;
  }
  if (video.kind === "vimeo") {
    return getSanityVideoEmbedId(src, "vimeo") || undefined;
  }
  return undefined;
}
function getBooleanOption(options: SanityVideoValue["options"], key: string, fallback = false) {
  const value = options?.[key];
  return typeof value === "boolean" ? value : fallback;
}
export function toCraftInlineVideoAsset(media?: SanityMediaValue | null): CraftAsset | undefined {
  const video = media?.kind === "video" ? media.video : undefined;
  const source = video?.kind === "air" || video?.kind === "file" ? video.source : undefined;
  const src = source?.src;
  if (!src) return undefined;
  const isHls = source.delivery === "hls";
  return {
    __typename: "default_Asset",
    mimeType: isHls ? "application/vnd.apple.mpegurl" : source.mimeType || "video/mp4",
    src,
    type: isHls ? "video/hls" : source.mimeType || "video/mp4"
  };
}
function toCraftVideoMedia(media: SanityMediaValue): CraftMediaBlock | undefined {
  const video = media.video;
  const src = video?.source?.src;
  if (!video || !src) {
    return undefined;
  }
  const coverAsset = toCraftAsset(video.cover);
  const base = {
    caption: media.caption,
    isDarkened: media.isDarkened
  };
  if (video.kind === "air" || video.kind === "file") {
    const videoAsset = toCraftInlineVideoAsset(media);
    if (!videoAsset) return undefined;
    return {
      ...base,
      __typename: "baseMedia_inlineVideo_BlockType",
      posterImage: coverAsset?.src ? [{
        src: coverAsset.src
      }] : undefined,
      videoAsset: [videoAsset]
    };
  }
  if (video.kind === "mux" || video.kind === "vimeo" || video.kind === "youtube") {
    const videoId = getVideoId(video);
    if (!videoId) {
      return undefined;
    }
    return {
      ...base,
      __typename: "baseMedia_externalVideo_BlockType",
      coverMedia: coverAsset ? [coverAsset] : undefined,
      showControls: getBooleanOption(video.options, "controls", getBooleanOption(video.options, "showControls")),
      videoId,
      videoProvider: video.kind
    };
  }
  return undefined;
}
export function toCraftMedia(media?: SanityMediaValue | null): CraftMediaBlock | undefined {
  const value = media as SanityImageLike | undefined;
  if (media?.kind === "video") {
    return toCraftVideoMedia(media);
  }
  const asset = toCraftAsset(media);
  if (!asset) {
    return undefined;
  }
  return {
    __typename: "baseMedia_image_BlockType",
    caption: value?.caption,
    imageAsset: [asset],
    isDarkened: value?.isDarkened
  };
}
export function toCraftMediaArray(media?: SanityMediaValue | null) {
  const resolved = toCraftMedia(media);
  return resolved ? [resolved] : [];
}